import type {ActivityVersion, ActivityCheckpoint, PublishActivity} from "./activity-library";
import type {InteractiveActivityPackage} from "./interactive-activity-package";
import type {InteractiveActivityBlock, InteractiveAssetRef, InteractiveCompetencyCheckpoint} from "./interactive-activity";
import {checkpointAnchorId, checkpointEvidenceIds, isEvidenceBlock} from "./interactive-activity-checkpoints";

export type ContentCheckpoint = Partial<InteractiveCompetencyCheckpoint & ActivityCheckpoint> & {id: string; competencyId: string};
export type UnifiedActivity = {
  id?: string; version?: number; title: string; levelId?: string; themeId?: string; chapterId?: string;
  levelTitle?: string; themeTitle?: string; chapterTitle?: string; coefficient?: number; competenceIds?: string[];
  blocks: InteractiveActivityBlock[]; assets: InteractiveAssetRef[]; competencyCheckpoints: ContentCheckpoint[];
};
export type NormalizedCheckpoint = ContentCheckpoint & {evidenceBlockIds: string[]};
export type NormalizedFlowItem = {type: "checkpoint"; id: string; checkpoint: NormalizedCheckpoint; position: number} |
  {type: InteractiveActivityBlock["type"]; id: string; block: InteractiveActivityBlock; position: number};
export type NormalizedActivity = Omit<UnifiedActivity, "competencyCheckpoints"> & {
  checkpoints: NormalizedCheckpoint[]; flow: NormalizedFlowItem[];
  capabilities: {hasStudentInputs: boolean; hasInteractiveContent: boolean; hasCheckpoints: boolean; hasEvidenceBlocks: boolean;
    supportsDirectEvaluation: boolean; requiresStudentSubmission: boolean};
};
export type ActivityInput = ActivityVersion | InteractiveActivityPackage | UnifiedActivity;

/** Pure, detached read model. Never resolves a newer version or consults a mutable catalogue. */
export function normalizeActivity(source: ActivityInput, includeHidden = false): NormalizedActivity {
  const raw = structuredClone(source);
  const isPackage = "format" in raw;
  const record = raw as UnifiedActivity & Partial<ActivityVersion>;
  const pkg = isPackage ? raw as InteractiveActivityPackage : record.package;
  const blocks = record.blocks ?? pkg?.interactive.blocks ?? [];
  const assets = record.assets ?? pkg?.interactive.assets ?? [];
  const experimentalMode = pkg?.interactive.experimentalMode;
  const modeVisible = (modes?: string[], part?:string) => {const mode=(part && pkg?.interactive.experimentalModeOverrides?.[part]) || experimentalMode;return !modes?.length || !mode || modes.includes(mode);};
  const seeds = (record.checkpoints ?? record.competencyCheckpoints ?? pkg?.interactive.competencyCheckpoints ?? [])
    .filter(c => includeHidden || modeVisible("experimentalModes" in c ? c.experimentalModes : undefined,"experimentalPart" in c ? c.experimentalPart : undefined));
  const checkpoints: NormalizedCheckpoint[] = seeds.map(c => {
    const metadata = record.competencyCheckpoints?.find(p => p.id === c.id) ?? pkg?.interactive.competencyCheckpoints?.find(p => p.id === c.id);
    const cp = {...metadata, ...c};
    return {...cp, evidenceBlockIds: [...checkpointEvidenceIds(cp)]};
  });
  const slots = new Map<number, NormalizedCheckpoint[]>();
  for (const cp of checkpoints) {
    const anchor = checkpointAnchorId(cp);
    const index = anchor ? blocks.findIndex(b => b.id === anchor) : -1;
    if (anchor && index < 0) throw new Error(`Checkpoint ${cp.id} : bloc d’ancrage inconnu.`);
    const slot = cp.flowPosition ?? (anchor ? index + 1 : blocks.length);
    if (!Number.isInteger(slot) || slot < 0 || slot > blocks.length || (anchor && slot !== index + 1))
      throw new Error(`Checkpoint ${cp.id} : position de flux invalide.`);
    slots.set(slot, [...(slots.get(slot) ?? []), cp]);
  }
  const flow: NormalizedFlowItem[] = [];
  for (let i = 0; i <= blocks.length; i++) {
    for (const cp of slots.get(i) ?? []) flow.push({type:"checkpoint",id:cp.id,checkpoint:cp,position:flow.length});
    if (i < blocks.length) flow.push({type:blocks[i].type,id:blocks[i].id,block:blocks[i],position:flow.length});
  }
  const activeBlocks = includeHidden ? blocks : blocks.filter(b => b.visible !== false && modeVisible(b.experimentalModes,b.experimentalPart));
  const activeFlow = includeHidden ? flow : flow.filter(item => item.type === 'checkpoint' || (item.block.visible !== false && modeVisible(item.block.experimentalModes,item.block.experimentalPart)));
  const hasStudentInputs = activeBlocks.some(isEvidenceBlock);
  const hasInteractiveContent = activeBlocks.some(b => ["simulation","animation"].includes(b.type) || b.type==="molecule"&&b.interactive!==false);
  const hasCheckpoints = checkpoints.length > 0;
  const supportsDirectEvaluation = !hasStudentInputs && hasCheckpoints && checkpoints.every(c =>
    c.evidenceBlockIds.length === 0 && c.evaluationMode !== "automatic_with_teacher_override");
  return {
    id: isPackage ? undefined : record.id, version: isPackage ? undefined : record.version,
    title: record.title ?? pkg?.activity.title ?? "", levelId: record.levelId, themeId:record.themeId, chapterId:record.chapterId,
    levelTitle:record.levelTitle ?? pkg?.activity.schoolLevel, themeTitle:record.themeTitle ?? pkg?.activity.theme,
    chapterTitle:record.chapterTitle ?? pkg?.activity.chapter, coefficient:record.coefficient ?? pkg?.activity.coefficient,
    competenceIds:record.competenceIds ?? pkg?.activity.competencyIds ?? [...new Set(checkpoints.map(c=>c.competencyId))],
    blocks: activeBlocks, assets, checkpoints, flow: activeFlow,
    capabilities:{hasStudentInputs,hasInteractiveContent,hasCheckpoints,hasEvidenceBlocks:hasStudentInputs || hasInteractiveContent,
      supportsDirectEvaluation,requiresStudentSubmission:!supportsDirectEvaluation},
  };
}

/** Only legacy draft compatibility needs the old type to infer missing content. */
export function normalizeActivityDraft(p: PublishActivity): NormalizedActivity {
  const checkpoints = p.competencyCheckpoints ?? p.package?.interactive.competencyCheckpoints ??
    (!p.package && p.blocks === undefined && p.activityType === "competency_only"
      ? p.competenceIds.map(id=>({id:`competence:${id}`,competencyId:id,evidenceBlockIds:[],rubric:p.criteria?.[id]})) : []);
  return normalizeActivity({...p,blocks:p.blocks ?? p.package?.interactive.blocks ?? [],
    assets:p.assets ?? p.package?.interactive.assets ?? [],competencyCheckpoints:checkpoints}, true);
}

/** Adapter for existing Studio/renderers, never persisted by a read operation. */
export function activityPackageForEditing(source: ActivityInput): InteractiveActivityPackage {
  if ("format" in source) return structuredClone(source);
  if ("package" in source && source.package) {
    const pkg = structuredClone(source.package);
    pkg.interactive.competencyCheckpoints = pkg.interactive.competencyCheckpoints?.map(c => ({...c,
      rubric: structuredClone(source.checkpoints.find(frozen => frozen.id === c.id)?.rubric ?? c.rubric)}));
    return pkg;
  }
  const n = normalizeActivity(source, true);
  return {format:"competenceo-interactive-activity",version:1,
    activity:{title:n.title,schoolLevel:n.levelTitle ?? "",theme:n.themeTitle ?? "",chapter:n.chapterTitle ?? "",coefficient:n.coefficient ?? 1,competencyIds:n.competenceIds ?? []},
    interactive:{schemaVersion:1,revision:1,status:"draft",flow:"linear",blocks:n.blocks,assets:n.assets,competencyRules:[],
      competencyCheckpoints:n.checkpoints.map(c=>({id:c.id,competencyId:c.competencyId,evidenceBlockIds:c.evidenceBlockIds,
        ...(c.questionIds ? {questionIds:c.questionIds,positionAfterQuestionId:c.positionAfterQuestionId} : {}),
        ...(checkpointAnchorId(c) ? {positionAfterBlockId:checkpointAnchorId(c)} : {}),rubric:c.rubric,flowPosition:c.flowPosition ?? (checkpointAnchorId(c) ? undefined : n.blocks.length),
        evaluationMode:c.evaluationMode ?? "manual",studentDisplay:c.studentDisplay ?? {showCompetenceName:true,showCriteria:true,showGradeBeforeValidation:false},
        feedback:c.feedback ?? {enabled:true,automatic:false}}))}};
}

/** Descriptive only: badges never select a workflow. */
export function activityBadges(activity: NormalizedActivity): string[] {
  const badges: string[] = [];
  if (activity.capabilities.hasInteractiveContent) badges.push("Interactive");
  if (!activity.blocks.length && activity.capabilities.hasCheckpoints && !activity.capabilities.hasStudentInputs)
    badges.push("Compétences");
  return badges;
}
